/**
 * Search autocomplete
 *
 * @param {HTMLElement} input - The input field to attach the autocomplete to
 * @param {HTMLElement} autocompleteContainer - The container to render the autocomplete results in
 * @param {Object} options - Additional options
 * @param {Function} options.inputFocusCallback - Callback function when the input field is focused
 * @param {Function} options.inputBlurCallback - Callback function when the input field is blurred
 * @param {Function} options.noResultsCallback - Callback function when no results are found
 * @param {Function} options.hasResultsCallback - Callback function when results are found
 * @param {Function} options.selectCallback - Callback function when a result is selected
 * @param {String} options.basePath - The base path for the AJAX request. Fetched from window.keSearchAutoCompleteCustom.basePath if not provided
 * @param {String} options.eID - The eID for the AJAX request. Fetched from window.keSearchAutoCompleteCustom.eID if not provided
 * @param {String} options.pid - The page ID for the AJAX request. Fetched from window.keSearchAutoCompleteCustom.pid if not provided
 * @param {Number} options.amount - The amount of results to fetch. Fetched from window.keSearchAutoCompleteCustom.amount if not provided or defaults to 10
 * @param {Boolean} options.renderResultAsLink - Render the result as a link
 * @param {String} options.searchResultLink - The link to use for the search result
 * @returns {void}
 */
export default class searchAutocomplete {
  constructor(input, autocompleteContainer, options = {}) {
    this.input = input;
    this.autocompleteContainer = autocompleteContainer;
    this.inputFocusCallback = options.inputFocusCallback || null;
    this.inputBlurCallback = options.inputBlurCallback || null;
    this.noResultsCallback = options.noResultsCallback || null;
    this.hasResultsCallback = options.hasResultsCallback || null;
    this.selectCallback = options.selectCallback || null;
    this.basePath = options.basePath || (window.keSearchAutoCompleteCustom && window.keSearchAutoCompleteCustom.basePath) || '';
    this.eID = options.eID || (window.keSearchAutoCompleteCustom && window.keSearchAutoCompleteCustom.eID) || '';
    this.pid = options.pid || (window.keSearchAutoCompleteCustom && window.keSearchAutoCompleteCustom.pid) || '';
    this.amount = options.amount || window.keSearchAutoCompleteCustom.amount || 4;
    this.renderResultAsLink = options.renderResultAsLink || false;
    this.searchResultLink = options.searchResultLink || null;
    this._init();
  }

  _init() {
    if (!this.basePath || !this.eID || !this.pid) return;
    this.input.addEventListener("input", this._handleInput.bind(this));
    this.input.addEventListener("focus", this._handleFocus.bind(this));
    this.input.addEventListener("blur", this._handleBlur.bind(this));
  }

  _handleInput() {
    const term = this.input.value.toLowerCase();
    this.term = term;
    if (term.length < 1) {
      this._clearContainer();
      if (this.noResultsCallback) {
        this.noResultsCallback();
      }
      return;
    }
    if (window.searchAutocompleteCache && window.searchAutocompleteCache[term]) {
      this._renderResults(window.searchAutocompleteCache[term]);
    } else {
      this._fetchResults(term);
    }
  }

  _fetchResults() {
    const term = this.term;
    const httpRequest = new XMLHttpRequest();
    if (!httpRequest) {
      alert("Giving up :( Cannot create an XMLHTTP instance");
      return false;
    }
    httpRequest.onreadystatechange = () => {
      if (httpRequest.readyState == 4) {
        if (!httpRequest.status.toString().match(/^2/)) {
          console.error("Request failed", httpRequest);
          return;
        }
        const response = JSON.parse(httpRequest.responseText);
        this._handleResponse(response);
      }
    };

    httpRequest.open(
      "GET",
      `${this.basePath}index.php?eID=${this.eID}&wordContains=${term}&amount=${this.amount}&pid=${this.pid}`,
    );
    httpRequest.send();
  }

  _handleResponse(response) {
    if (response.length === 0) {
      if (this.noResultsCallback) {
        this.noResultsCallback();
        return;
      }
      this._clearContainer();
    } else {
      this._handleCache(response);
      this._renderResults(response);
    }
  }

  _handleCache(response) {
    if (!window.searchAutocompleteCache) {
      window.searchAutocompleteCache = {};
    }
    window.searchAutocompleteCache[this.input.value] = response;
  }

  _renderResults(results) {
    this._clearContainer();
    if (this.hasResultsCallback) {
      this.hasResultsCallback();
    }
    results.forEach((result) => {
      const item = this._createResultItem(result);
      this.autocompleteContainer.appendChild(item);
    });
  }

  _createResultItem(result) {
    const term = this.term;
    let item = null;
    if (this.renderResultAsLink && this.searchResultLink) {
      item = document.createElement("a");
      item.href = `${this.searchResultLink}?tx_kesearch_pi1%5Bsword%5D=%20${result}`;
    } else {
      item = document.createElement("div");
    }
    item.classList.add("autocomplete-item");
    item.innerHTML = result.replace(new RegExp(term, "gi"), (match) => `<span>${match}</span>`);
    item.addEventListener("click", () => {
      if (!this.selectCallback) return;
      this.selectCallback(result);
    });
    return item;
  }

  _handleFocus() {
    if (!this.inputFocusCallback) return;
    this.inputFocusCallback();
  }

  _handleBlur() {
    if (!this.inputBlurCallback) return;
    this.inputBlurCallback();
  }

  _clearContainer() {
    this.autocompleteContainer.innerHTML = "";
  }
}
